import { APP_VERSION, PRODUCTION_GROUP_TAG } from '@kontor/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Archive,
  Bell,
  FileText,
  LayoutDashboard,
  Lock,
  PanelLeftClose,
  PanelLeftOpen,
  RadioTower,
  Rocket,
  Scissors,
  Search,
  Server as ServerIcon,
  Settings,
  WifiOff,
  type LucideIcon,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { NavLink } from 'react-router'

import { ThemeToggle } from '@/components/ThemeToggle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fetchHealth, fetchServers, fetchVaultStatus, lockVault } from '@/lib/api'
import { STATUS_DOT, STATUS_LABELS } from '@/lib/status'
import { cn } from '@/lib/utils'
import { connectionOf, useConnectionStore } from '@/store/connections'

interface NavEntry {
  to: string
  label: string
  icon: LucideIcon
  end: boolean
}

/** Die Trennstriche sitzen zwischen den Gruppen, nicht zwischen den Einträgen. */
const NAV: NavEntry[][] = [
  [{ to: '/', label: 'Übersicht', icon: LayoutDashboard, end: true }],
  [
    { to: '/server', label: 'Server', icon: ServerIcon, end: true },
    { to: '/projekte', label: 'Projekte', icon: Rocket, end: false },
  ],
  [
    { to: '/alarme', label: 'Alarme', icon: Bell, end: false },
    { to: '/backups', label: 'Backups', icon: Archive, end: false },
    { to: '/berichte', label: 'Berichte', icon: FileText, end: false },
  ],
  [
    { to: '/snippets', label: 'Snippets', icon: Scissors, end: false },
    { to: '/einstellungen', label: 'Einstellungen', icon: Settings, end: false },
  ],
]

const STORAGE_KEY = 'kontor.sidebar'

/**
 * Breite der Seitenleiste, über Neuladen hinweg gemerkt.
 *
 * Browser-Speicher kann fehlen oder beim Lesen werfen (privates Fenster, blockierte
 * Seitendaten). Deshalb in try/catch und mit einer Vorgabe, die ohne ihn richtig ist — es geht
 * hier um eine Bequemlichkeit, nicht um Zustand, der stimmen muss.
 */
function useSidebarWidth(): [boolean, () => void] {
  // Gleich beim ersten Rendern gelesen statt in einem Effekt: sonst steht die Leiste einen
  // Frame lang breit da und springt dann zu. Das sieht nach Ruckeln aus und ist es auch.
  const [rail, setRail] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'rail'
    } catch {
      // Kein Speicher (privates Fenster, blockierte Seitendaten): breit ist die richtige Vorgabe.
      return false
    }
  })

  const toggle = (): void => {
    setRail((value) => {
      const next = !value
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? 'rail' : 'wide')
      } catch {
        // Nicht merken zu können ist kein Grund, es nicht zu tun.
      }
      return next
    })
  }

  return [rail, toggle]
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  if (minutes >= 1) return `${String(minutes)} min`
  return `${String(seconds)} s`
}

function NavRow({ entry, rail }: { entry: NavEntry; rail: boolean }) {
  return (
    <NavLink
      to={entry.to}
      end={entry.end}
      title={rail ? entry.label : undefined}
      className={({ isActive }) =>
        cn(
          'ease-out-quint flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors duration-100',
          rail && 'justify-center px-0',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
            : 'text-muted-foreground hover:bg-sidebar-accent/40 hover:text-foreground',
        )
      }
    >
      <entry.icon className="size-4 shrink-0" />
      {rail ? <span className="sr-only">{entry.label}</span> : <span>{entry.label}</span>}
    </NavLink>
  )
}

/**
 * Die Server direkt in der Seitenleiste.
 *
 * Der eigentliche Gewinn der Umstellung: Von jeder Seite aus ist jeder Server einen Klick weit
 * weg, mit seinem Verbindungszustand daneben. Vorher musste man über die Serverliste gehen.
 */
function ServerTree({ rail }: { rail: boolean }) {
  const servers = useQuery({ queryKey: ['servers'], queryFn: fetchServers })
  const states = useConnectionStore((store) => store.states)

  if (rail || servers.data === undefined || servers.data.length === 0) return null

  return (
    <div className="mt-0.5 space-y-0.5 pl-3">
      {servers.data.map((server) => {
        const connection = connectionOf(states, server.id)
        return (
          <NavLink
            key={server.id}
            to={`/server/${server.id}/prozesse`}
            title={`${server.name} — ${STATUS_LABELS[connection.status]}`}
            className={({ isActive }) =>
              cn(
                'border-border/60 flex items-center gap-2 border-l py-1 pl-2.5 text-xs transition-colors duration-100',
                isActive
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            <span
              className={cn('size-1.5 shrink-0 rounded-full', STATUS_DOT[connection.status])}
              aria-hidden="true"
            />
            <span className="truncate">{server.name}</span>
            {server.groupTag === PRODUCTION_GROUP_TAG ? (
              <span className="bg-prod ml-auto size-1.5 shrink-0 rounded-full" title="Produktion" />
            ) : null}
          </NavLink>
        )
      })}
    </div>
  )
}

function Sidebar({ rail, onToggle }: { rail: boolean; onToggle: () => void }) {
  return (
    <aside
      className={cn(
        'bg-sidebar border-border ease-out-quint sticky top-0 flex h-screen shrink-0 flex-col border-r transition-[width] duration-200',
        rail ? 'w-14' : 'w-64',
      )}
    >
      <div className={cn('flex h-12 items-center gap-2 px-3', rail && 'justify-center px-0')}>
        {rail ? null : (
          <>
            <span className="text-sm font-semibold tracking-tight">Kontor</span>
            <span className="text-muted-foreground font-mono text-[10px]">v{APP_VERSION}</span>
          </>
        )}
        <Button
          size="icon-sm"
          variant="ghost"
          className={cn('text-muted-foreground', rail ? undefined : 'ml-auto')}
          onClick={onToggle}
          aria-label={rail ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
        >
          {rail ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {NAV.map((group, index) => (
          <div
            key={group[0]?.to ?? index}
            className={cn('space-y-0.5', index > 0 && 'border-border/70 mt-2 border-t pt-2')}
          >
            {group.map((entry) => (
              <div key={entry.to}>
                <NavRow entry={entry} rail={rail} />
                {entry.to === '/server' ? <ServerTree rail={rail} /> : null}
              </div>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  )
}

function TopBar({ onSearch }: { onSearch: () => void }) {
  const queryClient = useQueryClient()
  const live = useConnectionStore((store) => store.live)
  const health = useQuery({ queryKey: ['health'], queryFn: fetchHealth, refetchInterval: 30_000 })
  const vault = useQuery({
    queryKey: ['vault-status'],
    queryFn: fetchVaultStatus,
    // Hält den Countdown ehrlich, ohne dass die Oberfläche selbst einen Timer laufen lässt.
    refetchInterval: 15_000,
  })

  const lock = useMutation({
    mutationFn: lockVault,
    onSuccess: () => {
      useConnectionStore.getState().clear()
      void queryClient.invalidateQueries({ queryKey: ['vault-status'] })
    },
  })

  return (
    <header className="border-border bg-background/80 sticky top-0 z-30 flex h-12 items-center gap-2 border-b px-4 backdrop-blur print:hidden">
      <Button
        size="sm"
        variant="outline"
        onClick={onSearch}
        className="text-muted-foreground w-full max-w-64 justify-start gap-2 font-normal"
        aria-keyshortcuts="Control+K"
      >
        <Search />
        <span>Suchen</span>
        <kbd className="bg-muted border-border ml-auto hidden rounded border px-1 font-mono text-[10px] leading-4 sm:inline">
          Strg K
        </kbd>
      </Button>

      <div className="ml-auto flex items-center gap-1.5">
        {/* §3.1: Läuft das Backend auf einem VPS, gehen die SSH-Verbindungen von dort aus. Die
            Oberfläche muss sagen, wo „hier" ist. */}
        {health.data ? (
          <Badge
            variant="secondary"
            className="hidden lg:inline-flex"
            title={`Backend auf ${health.data.backend.hostname}`}
          >
            {health.data.backend.label}
          </Badge>
        ) : null}

        <Badge
          variant="outline"
          className={live ? undefined : 'text-status-warn'}
          title={live ? 'Zustände kommen in Echtzeit' : 'Der Live-Kanal ist unterbrochen'}
        >
          {live ? <RadioTower className="text-status-ok" /> : <WifiOff />}
          <span className="hidden sm:inline">{live ? 'Live' : 'Kein Live-Kanal'}</span>
        </Badge>

        {vault.data?.locksInSec === null || vault.data === undefined ? null : (
          <Badge variant="outline" title="Zeit bis zur automatischen Sperre" className="tabular">
            sperrt in {formatCountdown(vault.data.locksInSec)}
          </Badge>
        )}

        <ThemeToggle />

        <Button
          size="sm"
          variant="ghost"
          onClick={() => lock.mutate()}
          disabled={lock.isPending}
          aria-keyshortcuts="Control+Shift+L"
        >
          <Lock />
          <span className="hidden md:inline">Sperren</span>
        </Button>
      </div>
    </header>
  )
}

/**
 * Die Hülle: Seitenleiste links, schlanke Leiste oben, Inhalt rechts.
 *
 * Vorher lagen acht Navigationspunkte, drei Statusanzeigen und die Suche in *einer* umbrechenden
 * Zeile; auf einer Server-Seite kam die Reiterzeile als dritte dazu. Drei gestapelte
 * Navigationszeilen kosten nicht nur Platz, sie nehmen auch jede Orientierung: Man sieht nicht
 * mehr, wo man ist, sondern nur noch, was es alles gibt.
 */
export function AppShell({ onSearch, children }: { onSearch: () => void; children: ReactNode }) {
  const [rail, toggle] = useSidebarWidth()

  return (
    <div className="bg-background text-foreground flex min-h-screen">
      <div className="print:hidden">
        <Sidebar rail={rail} onToggle={toggle} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onSearch={onSearch} />
        <main className="mx-auto w-full max-w-[86rem] flex-1 space-y-5 px-6 py-6">{children}</main>
      </div>
    </div>
  )
}
